import { duration } from '@eturi/date-util'
import type { Writable, WritableDeep } from '@eturi/ts-helpers'
import { assertNotNullish, setIfNotEqual } from '@eturi/util'
import { keysToInt } from '@op/util'
import type { Draft, Selector } from '@reduxjs/toolkit'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { castDraft } from 'immer'
import forOwn from 'lodash/forOwn.js'
import { createSliceTransformer } from 'rtk-slice-transformer'
import { resetAction } from '../actions/index.js'
import { bindCreateAsyncThunkToState } from '../bindCreateAsyncThunkToState.js'
import type { HttpExtra } from '../http.js'
import { MockDeviceApps } from '../model-util/apps.js'
import {
	type AppDetails,
	type AppDetailsMap,
	type AppDetailsRes,
	genNotFoundAppDetails,
	type InitState,
	mapBuiltInRawAppInfoToDetails,
	mapRawAppInfoToDetails,
	type NormalizedAppsDataForDevices,
	type NormalizedDeviceApp,
	type RawAppsDataForDevices,
	SpecialApp,
} from '../types/index.js'

export type UserAppsState = InitState & {
	readonly apps: RawAppsDataForDevices
}

export const createUserAppsState = (): UserAppsState => ({
	apps: {},
	isInit: false,
})

export type AppsState = {
	readonly appDetailsMap: AppDetailsMap
	readonly pruneDetailsTs: number
	readonly userApps: { readonly [userId: string]: UserAppsState }
}

export type WithAppsState = {
	readonly apps: AppsState
}

const initialState: AppsState = {
	appDetailsMap: {},
	pruneDetailsTs: 0,
	userApps: {},
}

const PRUNE_DETAILS_INTERVAL = duration(7, 'd')

const ensureUserState = (s: Draft<AppsState>, userId: string) =>
	(s.userApps[userId] ||= castDraft(createUserAppsState()))

/**
 * TODO: We should add a few enhancements:
 *
 * - Allow persistence of this entire slice, by pruning `userApps`
 * - Add something that listens to users or userIds changing to do the pruning:
 * 	- fetchUsers.fulfilled
 *  - dispatch custom action when users are fetched
 *    - (e.g. `dispatch(filteredUserIdsAction(fetchedUsers)))`)
 * - Add windowing or another form of smart fetch to `fetchAppDetailsForUser` to avoid making
 *   too many concurrent requests.
 */
export const appsSlice = /*@__PURE__*/ createSlice({
	name: 'apps',
	initialState,
	reducers: {},
	extraReducers: (builder) =>
		builder
			.addCase(resetAction, () => initialState)
			.addCase(fetchAppDetailsForUser.fulfilled, (s, a) => {
				Object.assign(s.appDetailsMap, a.payload)

				/**
				 * Once a week, we prune any details that apply to apps that are no longer installed.
				 */
				if (s.pruneDetailsTs < Date.now() - PRUNE_DETAILS_INTERVAL) {
					pruneSuperfluousAppDetails(s)
					s.pruneDetailsTs = Date.now()
				}
			})
			.addCase(fetchAppsForUser.fulfilled, (s, a) => {
				const userAppsState = ensureUserState(s, a.meta.arg.userId)

				setIfNotEqual(userAppsState, 'apps', a.payload)
				userAppsState.isInit = true
			}),
})

/**
 * Used to periodically prune any details that apply to apps that are no longer installed.
 *
 * Creates the set of all bundle IDs from all users' apps and then deletes any details that
 * don't have a matching bundle ID.
 */
const pruneSuperfluousAppDetails = (state: Draft<AppsState>) => {
	const appDetailsMap = state.appDetailsMap
	const allBundleIds = new Set(MockDeviceApps.getMockAppBundleIds())

	forOwn(state.userApps, (userAppsState) => {
		forOwn(userAppsState.apps, (rawAppsData) => {
			forOwn(rawAppsData?.installed_apps, (_, bundleId) => {
				allBundleIds.add(bundleId)
			})
		})
	})

	forOwn(appDetailsMap, (_, bundleId) => {
		if (!allBundleIds.has(bundleId)) {
			delete appDetailsMap[bundleId]
		}
	})
}

export const appsSliceTransformer = /*@__PURE__*/ createSliceTransformer(appsSlice, (s) =>
	keysToInt(s, ['appDetailsMap', 'userApps']),
)

////////// Thunks //////////////////////////////////////////////////////////////

const createAsyncThunk = /*@__PURE__*/ bindCreateAsyncThunkToState<WithAppsState>()

/**
 * Gets the set of all bundle IDs for a user's apps.
 */
const getUserAppBundleIds = (apps: Maybe<RawAppsDataForDevices>): Set<string> => {
	const userAppBundleIds = new Set<string>()

	if (!apps) return userAppBundleIds

	forOwn(apps, (rawAppsData) => {
		forOwn(rawAppsData?.installed_apps, (_, bundleId) => {
			userAppBundleIds.add(bundleId)
		})
	})

	return userAppBundleIds
}

/**
 * Gets all bundle IDs that have missing or expired details.
 *
 * If there aree no apps, and we're allowed to use mock apps, we use the mock app bundle IDs.
 */
const getMissingOrExpiredBundleIds = (
	apps: Maybe<RawAppsDataForDevices>,
	details: AppDetailsMap,
	locale: string,
	withMockApps: boolean,
): Set<string> => {
	let appBundleIds = getUserAppBundleIds(apps)

	if (appBundleIds.size === 0 && withMockApps) {
		appBundleIds = new Set(MockDeviceApps.getMockAppBundleIds())
	}

	const missingOrExpiredBundleIds = new Set<string>()
	// We set the time we consider apps to be too old
	const ttl = Date.now() - duration(5, 'd')

	// Add any bundle ids that don't have details
	for (const bundleId of appBundleIds) {
		if (!details[bundleId]) missingOrExpiredBundleIds.add(bundleId)
	}

	forOwn(details, (app, bundleId) => {
		if (!app) return

		// NOTE: These were "new" values so cached versions previously may not have had these.
		//  We normalize so we can do the same comparison.
		const appLocale = app.locale || ''
		const fetchTs = app.fetchTs || 0

		if (fetchTs < ttl || appLocale !== locale) {
			missingOrExpiredBundleIds.add(bundleId)
		}
	})

	return missingOrExpiredBundleIds
}

type FetchAppDetailsForUserArg = {
	readonly locale: string
	readonly userId: string
	readonly withMockApps?: boolean
}

/**
 * Fetches all AppDetails for a user's installed apps (or mock apps if no apps are installed).
 *
 * TODO: We should consider adding windowing or another form of smart fetch to this to avoid
 * making too many concurrent requests.
 */
export const fetchAppDetailsForUser = /*@__PURE__*/ createAsyncThunk(
	'apps/details/fetch',
	async ({ locale, userId, withMockApps = true }: FetchAppDetailsForUserArg, { getState }) => {
		const state = getState()
		const fetchBundleIds = getMissingOrExpiredBundleIds(
			userAppsState$(state)[userId]?.apps,
			appDetailsMap$(state),
			locale,
			withMockApps,
		)
		const updatedAppDetailsMap: Writable<AppDetailsMap> = {}

		await Promise.all(
			[...fetchBundleIds].map(async (bundleId) => {
				try {
					const appInfo = await fetch(
						`https://lambda.ourpact.com/api/appinfo/${bundleId}?locale=${locale}`,
					).then((res): Promise<AppDetailsRes> => res.json())

					if (appInfo.status === 'success') {
						let appDetails: AppDetails

						switch (appInfo.meta.info_type) {
							case 'not_found': {
								// NOTE: We generate an empty payload for not found apps so there is something
								//  truthy in app details and we don't fetch for this app on every reload
								appDetails = genNotFoundAppDetails(locale)
								break
							}

							case 'builtin': {
								appDetails = mapBuiltInRawAppInfoToDetails(appInfo.info, locale)
								break
							}

							default: {
								// NOTE: This default tries to future-proof. If different values are added to
								//  RawAppMetaType (i.e. we start to distinguish between different app stores),
								//  older apps will still try to parse info, and fails will be handled by try/catch
								appDetails = mapRawAppInfoToDetails(appInfo.info, locale)
							}
						}

						updatedAppDetailsMap[bundleId] = appDetails
					}
				} catch {
					updatedAppDetailsMap[bundleId] = null
				}
			}),
		)

		return updatedAppDetailsMap as AppDetailsMap
	},
)

type FetchAppsForUserArg = HttpExtra & {
	readonly userId: string
}
export const fetchAppsForUser = /*@__PURE__*/ createAsyncThunk(
	'apps/user/fetch',
	async ({ userId, ...extra }: FetchAppsForUserArg, { dispatch, extra: { http } }) => {
		const rawAppsData = await dispatch(
			http.get<Maybe<RawAppsDataForDevices[]>>(`/installed_apps?user_id=${userId}`, extra),
		)

		assertNotNullish(rawAppsData)

		/**
		 * This returns a list of `RawAppsDataForDevices`. I'm not entirely sure why. We can flatten
		 * this into a single `RawAppsDataForDevices` object by calling `Object.assign` and spreading
		 * the list as arguments. This is a workaround to get the type to work.
		 *
		 * Essentially, we're turning:
		 * ```ts
		 * [
		 *  { [DeviceIdA]: RawAppsData },
		 *  { [DeviceIdB]: RawAppsData }
		 * ]
		 * ```
		 * into:
		 * ```ts
		 * {
		 *   [DeviceIdA]: RawAppsData,
		 *   [DeviceIdB]: RawAppsData
		 * }
		 * ```
		 */
		const rawAppsForDevices: RawAppsDataForDevices = Object.assign({}, ...rawAppsData)

		return rawAppsForDevices
	},
	{
		condition: (arg, api) => {
			if (!arg.force && userAppsState$(api.getState())[arg.userId]?.isInit) return false
		},
	},
)

/**
 * Sends a command that refreshes a device's app list while the user is engaged
 * with the app.
 * @deprecated Use `requestRefreshApps` instead, which is throttled.
 */
export const refreshUserApps = /*@__PURE__*/ createAsyncThunk(
	'apps/refresh',
	async (userId: string, { dispatch, extra: { http } }) => {
		await dispatch(
			http.post('/command', {
				data: {
					command: 'app_list',
					rule_type: 'query',
					user_id: userId,
				},
			}),
		)
	},
)

////////// Selectors ///////////////////////////////////////////////////////////

const state$ = <T extends WithAppsState>(s: T) => s.apps
const userAppsState$ = /*@__PURE__*/ createSelector(state$, (s) => s.userApps)

export const appDetailsMap$ = /*@__PURE__*/ createSelector(state$, (s) => s.appDetailsMap)

/**
 * NOTE: We don't export the state selector raw, because we to normalize the SpecialApp cases first.
 *  We could do this in the result we get from the server, but I don't want to have to migrate
 *  the model to a different shape / version.
 */
export const createUserAppsSelector = (userIdSelector$: Selector<any, Maybe<string>>) =>
	createSelector(
		userAppsState$,
		userIdSelector$,
		(userAppsState, userId): NormalizedAppsDataForDevices => {
			const rawAppData = (userAppsState[userId || ''] || createUserAppsState()).apps
			const normalizedAppsDataForDevices: WritableDeep<NormalizedAppsDataForDevices> = {}

			forOwn(rawAppData, (rawAppsData, deviceId) => {
				if (!rawAppsData) return

				const normalizedInstalledApps: WritableDeep<NormalizedAppsDataForDevices>[string]['installed_apps'] =
					(normalizedAppsDataForDevices[deviceId] = { ...rawAppsData, installed_apps: {} })
						.installed_apps

				const installedApps = rawAppsData?.installed_apps

				// lodash handles nullish, so we can non-null assert (!) to get correct types.
				forOwn(installedApps!, (rawDeviceApp, rawBundleId) => {
					if (!rawDeviceApp || rawBundleId === SpecialApp.APPLE_FACETIME) return

					const normalizedDeviceApp: Writable<NormalizedDeviceApp> = {
						...rawDeviceApp,
						bundleId: rawBundleId,
						detailsBundleId: rawBundleId,
					}

					if (rawBundleId === SpecialApp.APPLE_CAMERA) {
						normalizedDeviceApp.bundleId = SpecialApp.APPLE_COMBO
						normalizedDeviceApp.title = 'Camera/FaceTime'
					}

					normalizedInstalledApps[normalizedDeviceApp.bundleId] = normalizedDeviceApp
				})
			})

			return normalizedAppsDataForDevices
		},
	)
